import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { AdminTabs } from "../components/admin/AdminTabs";
import { BookingTotals } from "../components/admin/BookingTotals";
import { BookingsTable } from "../components/admin/BookingsTable";
import { CancelRefundDialog } from "../components/admin/CancelRefundDialog";
import { AvailabilityCsvButton } from "../components/admin/AvailabilityCsvButton";
import { MoveBookingDialog } from "../components/admin/MoveBookingDialog";
import { TestDataCleanup } from "../components/admin/TestDataCleanup";
import { ConsultantInviteDialog } from "../components/admin/ConsultantInviteDialog";
import { DeleteSelectedBookings } from "../components/admin/DeleteSelectedBookings";
import { MonitorStock } from "../components/admin/MonitorStock";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "paid", label: "Paid" },
  { key: "pending", label: "Awaiting payment" },
  { key: "released", label: "Released" },
  { key: "cancelled", label: "Cancelled / refunded" },
];

export default function AdminBookings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [target, setTarget] = useState(null);
  const [moving, setMoving] = useState(null);
  const [inviting, setInviting] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(() => {
    api.get("/admin/bookings").then(({ data }) => { setData(data); setSelected(new Set()); })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const all = data?.bookings || [];
    if (filter === "all") return all;
    if (filter === "cancelled") return all.filter((r) => r.state === "cancelled" || r.state === "refunded");
    return all.filter((r) => r.state === filter);
  }, [data, filter]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = (on) => setSelected((s) => { const n = new Set(s); rows.forEach((r) => (on ? n.add(r.id) : n.delete(r.id))); return n; });

  return (
    <div data-testid="admin-bookings-page" className="container-ccc py-12">
      <div className="eyebrow">Admin · {user?.email}</div>
      <h1 className="font-serif text-4xl mt-2">Bookings</h1>
      <AdminTabs active="/admin/bookings" />
      <div className="mt-6 flex flex-wrap gap-3"><AvailabilityCsvButton /><TestDataCleanup onDone={load} /></div>
      {error && <div className="mt-6 text-sm text-destructive" data-testid="admin-bookings-error">{error}</div>}
      {!data && !error && <div className="mt-10 text-ink-muted">Loading…</div>}
      {data && (
        <>
          <BookingTotals week={data.week} />
          <MonitorStock />
          <div className="mt-10 flex flex-wrap gap-2" data-testid="booking-filters">
            {FILTERS.map((f) => (
              <button key={f.key} type="button" onClick={() => setFilter(f.key)} data-testid={`booking-filter-${f.key}`}
                className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${filter === f.key ? "bg-bronze-dark text-white border-bronze-dark" : "bg-white border-stone-300 hover:border-bronze-dark"}`}>
                {f.label}
              </button>
            ))}
            <div className="ml-auto"><DeleteSelectedBookings ids={selected} rows={data.bookings} onDone={load} /></div>
          </div>
          <BookingsTable rows={rows} onCancel={setTarget} onMove={setMoving} onInvite={setInviting}
            selected={selected} onToggle={toggle} onToggleAll={toggleAll} />
        </>
      )}
      <ConsultantInviteDialog row={inviting} onClose={() => setInviting(null)} onDone={() => { setInviting(null); load(); }} />
      <MoveBookingDialog row={moving} onClose={() => setMoving(null)} onDone={() => { setMoving(null); load(); }} />
      <CancelRefundDialog row={target} onClose={() => setTarget(null)}
        onDone={(keepOpen) => { load(); if (!keepOpen) setTarget(null); }} />
    </div>
  );
}
